/**
 * ANNOUNCEMENT BANNER
 * 
 * Displays active announcements to users.
 * Fetches announcements based on user's subscription tier.
 */

import React, { useState, useEffect } from 'react';
import { X, Bell, AlertTriangle, Info, Gift, Megaphone } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useSubscription } from '../context/SubscriptionContext';

const ANNOUNCEMENT_ICONS = {
    info: Info,
    promo: Gift,
    alert: AlertTriangle,
    warning: Megaphone
};

const ANNOUNCEMENT_COLORS = {
    info: 'from-blue-600 to-cyan-600',
    promo: 'from-orange-500 to-red-500',
    alert: 'from-yellow-500 to-orange-500',
    warning: 'from-red-600 to-pink-600'
};

const AnnouncementBanner = () => {
    const [announcements, setAnnouncements] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [dismissed, setDismissed] = useState(() => {
        const saved = localStorage.getItem('dismissed_announcements');
        return saved ? JSON.parse(saved) : [];
    });

    const { currentTier } = useSubscription();

    // Fetch active announcements
    useEffect(() => {
        const fetchAnnouncements = async () => {
            try {
                const now = new Date().toISOString();

                const { data, error } = await supabase
                    .from('announcements')
                    .select('*')
                    .eq('is_active', true)
                    .or(`expires_at.is.null,expires_at.gt.${now}`)
                    .order('sent_at', { ascending: false })
                    .limit(5);

                if (error) {
                    console.warn('Error fetching announcements:', error);
                    return;
                }

                // Filter by target audience
                const userTier = (currentTier || 'STARTER').toLowerCase();
                const filtered = (data || []).filter(a => {
                    if (a.target_audience === 'all') return true;
                    if (a.target_audience === userTier) return true;
                    if (a.target_audience === 'ambassador' && userTier === 'ambassador') return true;
                    return false;
                });

                // Exclude dismissed announcements
                const active = filtered.filter(a => !dismissed.includes(a.id));
                setAnnouncements(active);

            } catch (err) {
                console.error('Error fetching announcements:', err);
            }
        };

        fetchAnnouncements();

        // Refresh every 5 minutes
        const interval = setInterval(fetchAnnouncements, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, [currentTier, dismissed]);

    // Handle dismiss
    const handleDismiss = (announcementId) => {
        const newDismissed = [...dismissed, announcementId];
        setDismissed(newDismissed);
        localStorage.setItem('dismissed_announcements', JSON.stringify(newDismissed));

        // Move to next announcement if available
        if (currentIndex >= announcements.length - 1) {
            setCurrentIndex(0);
        }
    };

    // Auto-rotate through announcements
    useEffect(() => {
        if (announcements.length <= 1) return;

        const interval = setInterval(() => {
            setCurrentIndex(prev => (prev + 1) % announcements.length);
        }, 8000); // 8 seconds per announcement

        return () => clearInterval(interval);
    }, [announcements.length]);

    // Don't render if no active announcements
    if (announcements.length === 0) return null;

    const current = announcements[currentIndex];
    if (!current) return null;

    const Icon = ANNOUNCEMENT_ICONS[current.type] || Bell;
    const gradientColor = ANNOUNCEMENT_COLORS[current.type] || ANNOUNCEMENT_COLORS.info;

    return (
        <div className={`bg-gradient-to-r ${gradientColor} relative overflow-hidden`}>
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-20">
                <div className="absolute top-0 left-0 w-20 h-20 border border-white/20 rounded-full -translate-x-1/2 -translate-y-1/2" />
                <div className="absolute bottom-0 right-0 w-32 h-32 border border-white/20 rounded-full translate-x-1/2 translate-y-1/2" />
            </div>

            <div className="relative flex items-center justify-between px-4 py-2">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="p-1.5 bg-white/20 rounded-lg flex-shrink-0">
                        <Icon className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-white truncate">
                            {current.title}
                        </p>
                        <p className="text-xs text-white/80 truncate">
                            {current.message}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                    {/* Pagination dots */}
                    {announcements.length > 1 && (
                        <div className="flex gap-1">
                            {announcements.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setCurrentIndex(i)}
                                    className={`w-1.5 h-1.5 rounded-full transition-all ${i === currentIndex ? 'bg-white' : 'bg-white/40'
                                        }`}
                                />
                            ))}
                        </div>
                    )}

                    {/* Dismiss button */}
                    <button
                        onClick={() => handleDismiss(current.id)}
                        className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                    >
                        <X className="w-4 h-4 text-white" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AnnouncementBanner;
